import { useCallback, useState } from 'react';
import dayjs from 'dayjs';
import {
  AllDayCalendarEvent,
  CalendarGrid,
  CalendarGridCreateEvent,
  CalendarGridRenderProp,
} from '@frontend/calendar-grid';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { SettingsRoutes, useSettingsNavigate } from '@frontend/settings-routing';
import { usePreference } from '@frontend/user-preferences';
import { useModalControl } from '@frontend/design-system';
import { AppointmentDragDropConfirmationModal } from '../../components/calendar-view/calendar-view-v3/appointment-drag-drop-confirmation-modal';
import {
  renderGridEventCard,
  renderGridEventTooltip,
} from '../../components/calendar-view/events-cards/grid-event-card-switch';
import { useColumnEntityReorderStore } from '../../components/calendar-view/providers';
import { DEFAULT_ZOOM_LEVEL } from '../../components/calendar-view/zoom/zoom-context';
import { OutOfOfficeEventCardModal } from '../../components/modals/out-of-office-event-card-modal';
import { isUnassignedColumnId } from '../../hooks/scheduler-v3/calendar-grid-v3-event-builders';
import { useCalendarGridColumnReorder } from '../../hooks/scheduler-v3/use-calendar-grid-column-reorder';
import { CalendarGridV3Props, useCalendarGridV3Data } from '../../hooks/scheduler-v3/use-calendar-grid-v3-data';
import { useCalendarGridV3EventDragDrop } from '../../hooks/scheduler-v3/use-calendar-grid-v3-event-drag-drop';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';
import { useCalendarStore } from '../../stores/calendar.store';
import { useSetCalendarGridEventsAsSelected } from '../../stores/schedule-appointment-info-panel.store';
import { CalendarEventType } from '../Calendar/types';
import { useCalendarGridAutoScroll } from './components/use-calendar-grid-auto-scroll';
import { OnboardingFlowEmptyCalendarComponent } from './onboarding-flow/onboarding-flow-empty-calendar-component';

type CalendarV3WithGridProps = CalendarGridV3Props & {
  isLoading: boolean;
  hasWSM: boolean;
  handleConfiguredCalendarEvent: (configuredCalendarEvent: CalendarEventType) => void;
  primaryTimezone?: string;
};

const NoEventsComponentAdapter = () => <OnboardingFlowEmptyCalendarComponent />;

const minutesToHourString = (totalMinutes: number) =>
  dayjs().startOf('day').add(totalMinutes, 'minutes').format('hh:mm A');

export const CalendarV3WithGrid = ({
  isLoading,
  hasWSM,
  handleConfiguredCalendarEvent,
  primaryTimezone,
  ...props
}: CalendarV3WithGridProps) => {
  const { t } = useTranslation('schedule');
  const calendarData = useCalendarGridV3Data(props);
  const { calendarEntityType, calendarDate } = useCalendarStore(['calendarEntityType', 'calendarDate']);
  const { getScopeName } = useAppScopeStore();
  const { isMultiLocation } = useSchedulingLocationInfo();
  const navigate = useSettingsNavigate();
  const [calendarSettings, setCalendarSettings] = usePreference('ScheduleCalendarSettings');
  const zoomLevel = calendarSettings?.zoomLevel ?? DEFAULT_ZOOM_LEVEL;
  const { isInReorderMode } = useColumnEntityReorderStore(['isInReorderMode']);
  const { modalProps, openModal } = useModalControl();
  const [allDayEventDetails, setAllDayEventDetails] = useState<{
    id: string;
    title: string;
    startDate: string;
    endDate: string;
    locationId: string;
    providerId?: string;
    providerName?: string;
    locationName?: string;
  } | null>(null);
  const { handleEventsSelect, selectedEventIds, deepLinkedEventId } = useSetCalendarGridEventsAsSelected(calendarDate);

  const handleAllDayEventClick = useCallback(
    (event: AllDayCalendarEvent) => {
      const locationId = event.groupId;
      const providerId = event.columnId;
      const providerName = providerId
        ? props.sortedProviders.find((provider) => provider.id === providerId)?.fullName ?? ''
        : '';

      setAllDayEventDetails({
        id: event.id,
        title: event.name,
        startDate: calendarDate,
        endDate: calendarDate,
        locationId,
        providerId,
        providerName,
        locationName: getScopeName(locationId),
      });
      openModal();
    },
    [calendarDate, getScopeName, openModal, props.sortedProviders]
  );

  // Column reorder support — pass base columnGroups from data, get back merged version
  const { onColumnReorder, columnGroups } = useCalendarGridColumnReorder(
    calendarEntityType,
    calendarData.columnGroups ?? []
  );

  // Event drag-drop support (V3 only) — uses enriched GridCalendarEvent[] from calendarData
  const { onEventDragStart, onEventDragEnd, isEventDraggable, dragStyles, dragDropState, confirmDrop, cancelDrop } =
    useCalendarGridV3EventDragDrop({
      events: calendarData.events,
      calendarDate,
      startHour: 0,
      sortedProviders: props.sortedProviders,
    });

  // Auto-scroll to the appropriate position after the calendar finishes loading
  const { wrapperRef } = useCalendarGridAutoScroll({
    events: calendarData.events,
    isLoading,
    calendarDate,
    zoomLevel,
    primaryTimezone,
  });

  const handleEventCreate = useCallback(
    ({ columnId, groupId, startMinutes, endMinutes }: CalendarGridCreateEvent) => {
      // columnId is the provider/operatory id
      const providerName = calendarData.columns.find((column) => column.id === columnId)?.name;

      handleConfiguredCalendarEvent({
        startHour: minutesToHourString(startMinutes),
        endHour: minutesToHourString(endMinutes),
        providerName,
        providerId: columnId,
        isValid: true,
        locationId: groupId,
        calendarDateValue: formatDate(calendarDate, 'YYYY-MM-DD'),
      });
    },
    [calendarData.columns, calendarDate, handleConfiguredCalendarEvent]
  );

  const handleColumnHeaderClick = useCallback(
    (columnId: string, groupId: string) => {
      const isUnassigned = isUnassignedColumnId(columnId);
      if (isUnassigned) return;
      const isProvider = calendarEntityType === 'provider';
      const route: SettingsRoutes = isProvider
        ? isMultiLocation
          ? '/schedule/manage-providers/:locationId/edit/:providerId'
          : '/schedule/manage-providers/edit/:providerId'
        : isMultiLocation
        ? '/schedule/operatories/:locationId/edit/:operatoryId'
        : '/schedule/operatories/edit/:operatoryId';
      const params = isProvider
        ? isMultiLocation
          ? { locationId: groupId, providerId: columnId }
          : { providerId: columnId }
        : isMultiLocation
        ? { locationId: groupId, operatoryId: columnId }
        : { operatoryId: columnId };

      navigate.navigate({ to: route, params });
    },
    [calendarEntityType, isMultiLocation, navigate]
  );

  const renderEmptyStateComponent = useCallback(
    (_groupId: string[], variant: 'no-events' | 'no-columns') => {
      if (isLoading) return null;

      if (variant === 'no-events') return null;

      return <NoEventsComponentAdapter />;
    },
    [isLoading]
  );

  return (
    <>
      <CalendarGrid
        {...calendarData}
        columnGroups={columnGroups}
        selectedEventIds={selectedEventIds}
        onEventsSelect={handleEventsSelect}
        renderEventCard={renderGridEventCard as CalendarGridRenderProp}
        renderEventTooltip={renderGridEventTooltip as CalendarGridRenderProp}
        onEventCreate={hasWSM ? handleEventCreate : undefined}
        onColumnReorder={onColumnReorder}
        onColumnHeaderClick={handleColumnHeaderClick}
        isColumnReorderActive={isInReorderMode}
        onEventDragStart={onEventDragStart}
        onEventDragEnd={onEventDragEnd}
        isEventDraggable={isEventDraggable}
        dragStyles={dragStyles}
        deepLinkedEventId={deepLinkedEventId}
        onAllDayEventClick={handleAllDayEventClick}
        renderEmptyStateComponent={renderEmptyStateComponent}
        shouldShowStripeBackground
        wrapperRef={wrapperRef}
        zoomConfig={{
          levels: [1, 2],
          zoomLevel,
          onZoomLevelChange: (level: number) => setCalendarSettings({ ...calendarSettings, zoomLevel: level }),
          showControls: true,
        }}
      />
      {allDayEventDetails && (
        <OutOfOfficeEventCardModal
          id={allDayEventDetails.id}
          eventType='outOfOffice'
          title={allDayEventDetails.title}
          duration={t('All day')}
          locationId={allDayEventDetails.locationId}
          locationName={allDayEventDetails.locationName}
          providerId={allDayEventDetails.providerId}
          providerName={allDayEventDetails.providerName}
          startDate={allDayEventDetails.startDate}
          endDate={allDayEventDetails.endDate}
          calendarDate={calendarDate}
          modalProps={modalProps}
        />
      )}
      <AppointmentDragDropConfirmationModal
        pendingDropPayload={dragDropState.pendingDropPayload}
        show={dragDropState.pendingDropPayload !== null}
        onClose={cancelDrop}
        onConfirm={confirmDrop}
        gridEvents={calendarData.events}
        calendarDate={calendarDate}
        startHour={0}
      />
    </>
  );
};
